import api from './axiosConfig';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OptInStatusFilter = 'All' | 'OptedIn' | 'Missed' | 'NotCompensated';
export type OptInStatus = 'None' | 'OptedIn' | 'OptedOut';

// Amounts are in øre.
export interface OptInPartner {
  businessContentfulId: string;
  businessName: string | null;
  isOptInRequired: boolean;
  optedInCount: number;
  optedOutCount: number;
  missedCount: number;
  missedTransactionsCount: number;
  missedAmount: number;
  compensatedPeopleCount: number;
  compensatedRidesCount: number;
  compensatedCashback: number;
}

export interface OptInCustomer {
  identityId: string;
  name: string | null;
  email: string | null;
  optInStatus: OptInStatus;
  optedInAt: string | null;
  optedOutAt: string | null;
  isDeleted: boolean;
  paidCount: number;
  paidCashback: number;
  missedCount: number;
  missedAmount: number;
  compensatedCount: number;
  compensatedCashback: number;
  notCompensatedCount: number;
}

export interface OptInCustomersPage {
  pageNumber: number;
  pageSize: number;
  total: number;
  data: OptInCustomer[];
  counts: { all: number; optedIn: number; missed: number; notCompensated: number };
}

export type OptInTransactionStatus =
  | 'Paid'
  | 'Compensated'
  | 'NotOptedIn'
  | 'OtherFailure'
  | 'ManualCredit';

export type OptInTransactionStatusFilter = 'All' | OptInTransactionStatus;

export const OPT_IN_TRANSACTION_STATUS_LABELS: Record<OptInTransactionStatus, string> = {
  Paid: 'Paid',
  Compensated: 'Compensated',
  NotOptedIn: 'Not opted in',
  OtherFailure: 'Other failure',
  ManualCredit: 'Manual credit',
};

// Amounts are in øre.
export interface OptInTransaction {
  rowId: string;
  transactionAt: string;
  identityId: string | null;
  name: string | null;
  email: string | null;
  isDeleted: boolean;
  source: string;
  amount: number;
  cashback: number | null;
  status: OptInTransactionStatus;
  errorCode: string | null;
  compensatedAt: string | null;
}

// The person a transactions page is filtered to; name and email are null for an unknown identity.
export interface OptInTransactionPerson {
  identityId: string;
  name: string | null;
  email: string | null;
  isDeleted: boolean;
  optInStatus: OptInStatus;
}

export interface OptInTransactionsPage {
  pageNumber: number;
  pageSize: number;
  total: number;
  data: OptInTransaction[];
  counts: {
    all: number;
    paid: number;
    compensated: number;
    notOptedIn: number;
    otherFailure: number;
    manualCredit: number;
  };
  paidCashback: number;
  compensatedCashback: number;
  // Set when the page is filtered to one identity, even if they have no rows.
  person: OptInTransactionPerson | null;
}

export interface OptInTransactionFilters {
  status: OptInTransactionStatusFilter;
  identityId?: string;
  search?: string;
  // Full ISO instants, e.g. 2026-10-01T22:00:00.000Z.
  from?: string;
  to?: string;
}

// ─── API calls ────────────────────────────────────────────────────────────────

export const getOptInPartners = async (): Promise<OptInPartner[]> => {
  const response = await api.get('/opt-ins/admin/partners');
  return response.data;
};

export const getOptInCustomers = async (
  businessContentfulId: string,
  params: { status: OptInStatusFilter; search?: string; pageNumber: number; pageSize: number }
): Promise<OptInCustomersPage> => {
  const response = await api.get(
    `/opt-ins/admin/${encodeURIComponent(businessContentfulId)}/customers`,
    { params }
  );
  return response.data;
};

export const getOptInTransactions = async (
  businessContentfulId: string,
  params: OptInTransactionFilters & { pageNumber: number; pageSize: number }
): Promise<OptInTransactionsPage> => {
  const response = await api.get(
    `/opt-ins/admin/${encodeURIComponent(businessContentfulId)}/transactions`,
    { params }
  );
  return response.data;
};

const DEFAULT_CSV_FILENAME = 'opt-ins.csv';

// Cross-origin responses only expose Content-Disposition when the server lists it in
// Access-Control-Expose-Headers; otherwise the header is unreadable and we fall back.
const filenameFromContentDisposition = (header: unknown, fallback: string): string => {
  if (typeof header !== 'string') return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : fallback;
};

const saveCsv = (response: { data: BlobPart; headers: Record<string, unknown> }, fallbackFileName: string): void => {
  const filename = filenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFileName
  );
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadOptInCustomersCsv = async (
  businessContentfulId: string,
  params: { status: OptInStatusFilter; search?: string },
  fallbackFileName: string = DEFAULT_CSV_FILENAME
): Promise<void> => {
  const response = await api.get(
    `/opt-ins/admin/${encodeURIComponent(businessContentfulId)}/customers/export`,
    { params, responseType: 'blob' }
  );
  saveCsv(response, fallbackFileName);
};

export const downloadOptInTransactionsCsv = async (
  businessContentfulId: string,
  params: OptInTransactionFilters,
  fallbackFileName: string = 'opt-in-transactions.csv'
): Promise<void> => {
  const response = await api.get(
    `/opt-ins/admin/${encodeURIComponent(businessContentfulId)}/transactions/export`,
    { params, responseType: 'blob' }
  );
  saveCsv(response, fallbackFileName);
};
